<template>
  <div class="crud-table">
    <el-row class="page-buttons">
      <editor-button v-if="allowActions && allowCreate && hasCreatePermission" action="add" @click="handleCreate" />
      <editor-button v-if="filtersActive && showClearFilter" action="clearFilters" @click="clearAllFilters" />
      <span v-for="(action, i) in customActions.filter(a => !a.inRow)" :key="i" style="margin-left:10px">
        <editor-button
          action="primary"
          :type="action.type"
          :button-translation="action.buttonTranslation"
          :icon-only="false"
          @click="action.handleClick"
        />
      </span>
      <editor-button v-if="showClose && !isEmbedded" action="cancel" style="float:right" @click="close" />
    </el-row>
    <pagination
      v-if="usePagination"
      v-show="numberOfItems > 0 || alwaysShowPagination"
      :total="numberOfItems"
      :page.sync="listQuery.page"
      :limit.sync="listQuery.limit"
      @pagination="refreshList"
    />
    <div ref="tableContainer">
      <el-table
        ref="crudTable"
        v-loading="loading"
        :data="items"
        border
        :show-summary="showSummaryRow"
        :summary-method="getSummaries"
        fit
        highlight-current-row
        style="width: 100%"
        :max-height="getMaxTableHeight()"
        @row-dblclick="rowDblClick"
        @row-contextmenu="openContextMenu"
        @sort-change="sortChange"
      >
        <div v-for="(item, i) in items" :key="`${i}-${item.id}`"></div>
        <el-table-column
          v-for="column in columns"
          :key="getColumnFieldFromColumn(column)"
          label-class-name="filter-cell"
        >
          <template v-if="!disableFiltering" slot="header" slot-scope="{}">
            <el-input
              v-if="getFilterTypeFromColumn(column) === 'search'"
              v-model="search[getColumnFieldFromColumn(column)]"
              class="search"
              size="mini"
              :placeholder="$t('table.searchPlaceholder')"
              clearable
              prefix-icon="el-icon-search"
            />
            <el-select
              v-if="getFilterTypeFromColumn(column) === 'select'"
              v-model="search[getFilterNameFromColumn(column)]"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option
                v-for="item in column.filter.options"
                :key="item[column.filter.valueField]"
                :label="item[column.filter.labelField]"
                :value="item[column.filter.valueField]"
              />
            </el-select>
            <el-date-picker
              v-if="getFilterTypeFromColumn(column) === 'date'"
              v-model="search[getColumnFieldFromColumn(column)]"
              type="date"
              class="search"
              :placeholder="$t('table.searchDatePlaceholder')"
              size="mini"
              :format="dateFormat"
              :value-format="dateFormat"
              clearable
              style="width: 100%"
            />
            <el-checkbox
              v-if="getFilterTypeFromColumn(column) === 'checkbox'"
              v-model="search[getColumnFieldFromColumn(column) + 'Restricted']"
              :label="$t(getFilterLabelFromColumn(column))"
              class="checkbox"
              size="mini"
              clearable
            >
            </el-checkbox>
          </template>
          <el-table-column
            :prop="getColumnFieldFromColumn(column)"
            :sortable="!disableSorting && (column.sortable === undefined ? 'custom' : column.sortable)"
            :label="$t(column.label || 'common.' + getColumnFieldFromColumn(column))"
            :class-name="column.class"
            :align="column.align || defaultCellAlign"
            :width="column.width"
            :min-width="column.minWidth"
          >
            <template slot-scope="scope">
              <span v-if="!column.type">{{ getFormattedValueFromColumn(column, scope.row) }}</span>
              <span v-if="column.type === 'dateTime'">
                <date-time-picker v-model="scope.row[column.field]" :format="dateTimeFormat" disabled />
              </span>
              <span v-if="column.type === 'date'">
                <date-time-picker v-model="scope.row[column.field]" :format="dateFormat" disabled />
              </span>
              <span v-if="column.type === 'currency'">
                <numeric-input v-model="scope.row[column.field]" :precision="2" readonly />
              </span>
              <span v-if="column.type === 'minutes'">
                <numeric-input v-model="scope.row[column.field]" :precision="0" readonly />
              </span>
              <span v-if="column.type === 'link'">
                <el-link :href="column.href" :icon="column.icon" type="primary">{{ column.field }}</el-link>
              </span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column
          v-if="showActionIconsInList && allowActions"
          align="center"
          :label="$t('table.actions')"
          :width="actionsColumnWidth"
          min-width="75"
        >
          <template slot-scope="scope">
            <slot name="actions" v-bind="scope" />
            <span v-if="!!customActions" class="custom-actions">
              <el-button
                v-for="(customAction, i) in customActions.filter(a => a.inRow)"
                :key="i"
                :type="customAction.type || 'primary'"
                :icon="customAction.icon"
                circle
                @click="customAction.handleClick(scope.row)"
              />
            </span>
            <el-button
              v-if="allowEdit && hasEditPermission"
              type="primary"
              icon="el-icon-edit"
              circle
              @click="handleEdit(scope.row)"
            />
            <el-button
              v-if="allowDelete && hasDeletePermission"
              type="danger"
              icon="el-icon-delete"
              circle
              @click="deleteConfirm(scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
      <context-menu ref="contextMenu" :options="contextMenuOptions" @option-clicked="handleContextMenuOptionClicked" />
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import EditorButton from './EditorButton';
import DateTimePicker from '@/components/DSE/DateTimePicker';
import NumericInput from '@/components/DSE/NumericInput';
import { hasCreatePermissionForCode, hasEditPermissionForCode } from '@/utils/permission';
import { entityToStore } from '@/utils/store';
import { closeCurrentTag } from '@/utils/tags';
import ContextMenu from '@/components/DSE/ContextMenu';

const debounce = require('lodash.debounce');

import CrudTablePersistentState from './CrudTablePersistentState';
const persistentState = new CrudTablePersistentState();

export default {
  name: 'CrudTable',
  components: {
    Pagination,
    EditorButton,
    DateTimePicker,
    NumericInput,
    ContextMenu
  },
  props: {
    allowActions: {
      default: true,
      type: Boolean
    },
    allowEdit: {
      default: true,
      type: Boolean
    },
    allowCreate: {
      default: true,
      type: Boolean
    },
    allowDelete: {
      default: true,
      type: Boolean
    },
    alwaysShowPagination: {
      default: false,
      type: Boolean
    },
    customActions: {
      default() {
        return [];
      },
      type: Array
    },
    disableFiltering: {
      default: false,
      type: Boolean
    },
    disableSorting: {
      default: false,
      type: Boolean
    },
    columns: {
      /**
       * Array of Objects, Strings
       * Object keys: field: String, label: String, type: String, sortable: Boolean
       *  */
      default() {
        return {};
      },
      type: Array
    },
    entity: {
      default: '',
      type: String
    },
    getSummaries: {
      type: Function
    },
    onRowDblClick: {
      type: Function
    },
    onOpenContextMenu: {
      type: Function
    },
    onContextMenuOptionClicked: {
      type: Function
    },
    onCreate: {
      type: Function
    },
    onEdit: {
      type: Function
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    showClearFilter: {
      default: true,
      type: Boolean
    },
    loadDataOnCreated: {
      default: true,
      type: Boolean
    },
    generateSummary: {
      type: Function,
      default: ({ id, name, code, description, reference }) => {
        if (name === undefined) {
          let r = `${code || id}`;
          if (description) {
            r += ` (${description})`;
          }
          return r;
        }
        if (reference || code || description) {
          return `${name} (${reference || code || description})`;
        }
        return `${name}`;
      }
    },
    itemFilter: {
      type: Function,
      default: () => true
    },
    forceAllLocations: {
      type: Boolean,
      default: false
    },
    usePagination: {
      type: Boolean,
      default: true
    },
    inactiveID: {
      type: Number,
      default: 0
    },
    isEmbedded: {
      type: Boolean,
      default: false
    },
    persistState: {
      type: Boolean,
      default: true
    },
    permissionCode: String,
    actionsColumnWidth: {
      type: Number,
      default: 100
    },
    showActionIconsInList: {
      type: Boolean,
      default: true
    },
    showSummaryRow: {
      type: Boolean,
      default: false
    },
    actionBusy: {
      type: Boolean,
      default: false
    },
    showClose: {
      type: Boolean,
      default: true
    },
    limit: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      entityStoreName: entityToStore(this.entity),
      loadedData: { items: [], total: 0 },
      listQuery: persistentState.getDefaultListQuery(this.limit),
      search: persistentState.getDefaultSearch(this.filters, this.$route.params.filters || {}),
      sort: persistentState.getDefaultListQuery(this.limit).sort,
      total: 0,
      defaultCellAlign: 'left',
      contextMenuOptions: []
    };
  },
  computed: {
    dateFormat() {
      return this.$store.getters.dateFormat;
    },
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    hasCreatePermission() {
      return hasCreatePermissionForCode(this.permissionCode || this.entity, this.$store.getters.permissions);
    },
    hasDeletePermission() {
      return this.hasCreatePermission;
    },
    hasEditPermission() {
      return hasEditPermissionForCode(this.permissionCode || this.entity, this.$store.getters.permissions);
    },
    dontUseStore() {
      return !this.allowActions;
    },
    items() {
      const loadedItems = this.dontUseStore
        ? this.loadedData.items
        : this.$store.getters[this.entityStoreName + '/' + this.storeRetrieveItemsGetter];
      return loadedItems.filter(item => this.itemFilter(item, this));
    },
    listParams() {
      const params = {};
      params.query = { ...this.filters };
      if (this.usePagination) {
        params.query = { ...params.query, ...this.listQuery };
      }
      if (this.inactiveID > 0) {
        params.query.includeID = this.inactiveID;
      }
      if (this.sort) {
        params.query.sort = this.sort;
      }
      if (this.forceAllLocations) {
        params.query.forceAllLocations = this.forceAllLocations;
      }

      if (Object.keys(this.search).length > 0) {
        const formattedSearch = { ...this.search };
        this.columns.forEach(column => {
          if (column && column.field && column.type === 'currency' && formattedSearch[column.field]) {
            formattedSearch[column.field] = formattedSearch[column.field].replace(',', '.');
          }
        });
        params.query = { ...params.query, ...formattedSearch };
      }
      params.options = { dontSave: this.dontUseStore };
      return params;
    },
    loading() {
      return this.$store.getters[this.entityStoreName + '/isLoading'] || this.actionBusy;
    },
    numberOfItems() {
      return this.dontUseStore ? this.loadedData.total : this.$store.getters[this.entityStoreName + '/totalItemsCount'];
    },
    storeFetchListAction() {
      if (this.usePagination) {
        return this.dontUseStore ? 'getPaginatedItems' : 'replacePaginatedItems';
      }
      return this.dontUseStore ? 'getItems' : 'replaceItems';
    },
    storeRetrieveItemsGetter() {
      if (this.usePagination) {
        return 'loadedPaginatedItems';
      }
      return 'loadedItems';
    },
    formatters() {
      return this.columns.reduce((acc, column) => {
        acc[column.field || column] = column.formatter || ((row, field) => row[field]);
        return acc;
      }, {});
    },
    filtersActive() {
      return !this.disableFiltering && Object.keys(this.search).some(k => this.search[k]);
    }
  },
  watch: {
    search: {
      deep: true,
      handler: function(value) {
        this.listQuery.page = 1;
        this.debouncedUpdateSearch();
      }
    },
    filters(filters) {
      Object.keys(filters).forEach(key => {
        this.$set(this.search, key, filters[key]);
      });
    },
    listParams(listParameters) {
      persistentState.persistListParams(listParameters);
    }
  },
  beforeCreate() {
    persistentState.setNameSpace(`${this.$options.propsData.entity}_${this.$store.state.authentication.userId}`);
    if (this.$options.propsData.persistState !== undefined) {
      persistentState.enable(this.$options.propsData.persistState);
    } else {
      persistentState.enable(true);
    }
  },
  created() {
    const routeFilters = this.$route.params.filters;
    if (routeFilters) {
      for (const filter in routeFilters) {
        this.search[filter] = routeFilters[filter];
      }
      persistentState.persistListParams(this.listParams);
    }
    if (this.loadDataOnCreated) {
      this.refreshList();
    }
    this.debouncedUpdateSearch = debounce(this.refreshList, 500);
  },
  methods: {
    getFilterTypeFromColumn(column) {
      const { filterable, filter } = column;
      if (filterable === false) {
        // Not filterable if explicitly set so
        return undefined;
      }
      const givenType = filter && filter.type;
      return givenType || 'search';
    },
    getFilterLabelFromColumn(column) {
      const { filterable, filter } = column;
      if (filterable === false) {
        return undefined;
      }
      const label = filter && filter.label;
      return label || 'table.withRestriction';
    },
    getFormattedValueFromColumn(column, row) {
      const field = this.getColumnFieldFromColumn(column);
      return this.formatters[field](row, field, row[field]);
    },
    getColumnFieldFromColumn(column) {
      return column.field || column;
    },
    getFilterNameFromColumn(column) {
      return column.filter.filterName || this.getColumnFieldFromColumn(column);
    },
    deleteConfirm(row) {
      const { id } = row;
      const summary = this.generateSummary(row);
      this.$store
        .dispatch('notify/userBlockable', { notificationCode: 'deleteConfirm', params: { summary } })
        .then(() => {
          this.$store
            .dispatch(this.entityStoreName + '/deleteItem', {
              id: id
            })
            .then(() => {
              this.$store.dispatch('notify/deleteCompleted');
              this.refreshList();
            });
        })
        .catch(() => {
          this.$store.dispatch('notify/deleteCancelled');
        });
    },
    handleCreate() {
      this.onCreate ? this.onCreate() : this.$router.push({ name: 'Create' + this.entity });
      this.$emit('create');
    },
    handleEdit(row) {
      const { id } = row;
      this.onEdit ? this.onEdit(row) : this.$router.push({ name: 'Edit' + this.entity, params: { id } });
      this.$emit('edit', id);
    },
    openContextMenu(row, column, event) {
      if (this.onOpenContextMenu) {
        event.preventDefault();
        this.contextMenuOptions = this.onOpenContextMenu(row, column, event);
        this.$refs.contextMenu.showMenu(this.$el, event, row);
      }
    },
    handleContextMenuOptionClicked({ item, option }) {
      if (this.onContextMenuOptionClicked) {
        return this.onContextMenuOptionClicked(item, option);
      }
    },
    rowDblClick(row) {
      if (this.onRowDblClick) {
        this.onRowDblClick(row);
      } else {
        this.handleEdit(row);
      }
      this.$emit('input', row);
    },
    sortChange({ column, prop, order }) {
      if (prop !== null) {
        this.sort = order && (order === 'descending' ? prop + '_desc' : prop);
      }
      this.refreshList();
    },
    refreshList() {
      this.$store.dispatch(this.entityStoreName + '/' + this.storeFetchListAction, this.listParams).then(data => {
        if (data) {
          this.loadedData.items = Object.freeze(data.items);
          this.loadedData.total = data.total;
        }
      });
    },
    clearAllFilters() {
      this.$refs.crudTable.clearFilter();
      this.search = {};
    },
    close() {
      closeCurrentTag(this);
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - 40;
      }
    }
  }
};
</script>
<style lang="scss">
.el-table--medium td,
.el-table--medium th {
  padding: 0px 0;
}

.crud-table {
  .edit-input {
    padding-right: 100px;
  }

  .cancel-btn {
    position: absolute;
    right: 15px;
    top: 10px;
  }
  .custom-actions + .el-button {
    margin-left: 10px;
  }
  .filter-cell > div {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .hide-overflow {
    .cell {
      white-space: nowrap;
    }
  }
  .search {
    .el-input__inner {
      padding-left: 25px;
      padding-right: 20px;
    }
    .el-input__prefix {
      left: 0;
    }
    .el-input__suffix {
      right: 0;
    }
  }
  .search-select {
    width: 100%;
    .el-input__inner {
      padding-left: 5px;
    }
    .el-input {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
  .el-table .cell {
    word-break: break-word;
  }
  .checkbox {
    padding-left: 12px;
  }
}
</style>
